import {
  CancelledError,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';

import { isAbortError } from '@vestigas/data';

type EnsureQueryDataArgument = Parameters<QueryClient['ensureQueryData']>[0];

/**
 * `query` shares its in-flight fetch with any observer of the same
 * query. A query function that reads `AbortSignal` is cancelled when the last
 * observer unsubscribes, and with no cached data that rejection is
 * `CancelledError`. Route loaders await this fetch, so the cancellation fails
 * the route. React Strict Mode unsubscribes during mount, which is enough to
 * trigger it. The page subscribes again and fetches.
 */
export async function ensureQueryDataIgnoringCancellation(
  queryClient: QueryClient,
  options: { queryKey: QueryKey },
) {
  try {
    // `queryOptions()` returns a type `query` accepts, but that
    // type is not assignable to the collapsed overload parameter.
    return await queryClient.query({
      ...options,
      staleTime: 'static',
    } as EnsureQueryDataArgument);
  } catch (error) {
    if (error instanceof CancelledError || isAbortError(error)) {
      return queryClient.getQueryData(options.queryKey);
    }

    throw error;
  }
}
